import React, { useState, useEffect } from 'react';
import {
    EuiCard,
    EuiCollapsibleNavGroup,
    EuiFlexGroup,
    EuiFlexItem,
    EuiIcon,
    EuiTitle,
    EuiText,
    EuiSpacer,
    EuiButtonIcon,
    EuiPanel,
    EuiFormRow,
    EuiSelect,
    EuiHorizontalRule,
} from '@elastic/eui';
import { useFieldArray } from 'react-hook-form';
import SwitchField from '../../../../components/Form/SwitchField';
import NumberField from '../../../../components/Form/NumberField';
import TextField from '../../../../components/Form/TextField';

const TierSystem = ({ control, errors, watch, setValue, selectedSystem }) => {
    const [isNavOpen, setIsNavOpen] = useState(true);
    const [tierCount, setTierCount] = useState(watch('tier_count') || 3);
    const enableTierSystem = watch('enable_tier_system');

    const { fields: tierFields, replace: replaceTiers } = useFieldArray({
        control,
        name: 'tiers',
    });
    const tierCountOptions = [
        { value: 2, text: '2 Tiers' },
        { value: 3, text: '3 Tiers' },
        { value: 4, text: '4 Tiers' },
        { value: 5, text: '5 Tiers' },
    ];

    // Default tier templates
    const defaultTierNames = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'];
    const defaultTierThresholds = [0, 5000, 15000, 30000, 50000];
    const defaultCashbackRates = [2, 3, 5, 7, 10];
    const defaultPointsAmounts = [100, 75, 50, 25, 10];

    useEffect(() => {
        if (enableTierSystem && tierCount !== tierFields.length) {
            const newTiers = [];
            for (let i = 0; i < tierCount; i++) {
                const existingTier = tierFields[i];
                newTiers.push({
                    id: i + 1,
                    name: existingTier?.name || defaultTierNames[i],
                    ltv_threshold: existingTier?.ltv_threshold || defaultTierThresholds[i],
                    cashback_rate: existingTier?.cashback_rate || defaultCashbackRates[i],
                    points_earning_amount: existingTier?.points_earning_amount || defaultPointsAmounts[i],
                    points_earning_points: existingTier?.points_earning_points || 1,
                });
            }
            replaceTiers(newTiers);
        }
    }, [tierCount, enableTierSystem]);

    const validateTierThreshold = (value, index) => {
        if (index === 0) return value === 0 || 'First tier must start at 0';

        const tiers = watch('tiers');
        if (index > 0 && tiers[index - 1]) {
            const prevThreshold = tiers[index - 1].ltv_threshold;
            return value > prevThreshold || 'Must be greater than previous tier';
        }
        return true;
    };
    return (
        <EuiCard
            layout='horizontal'
            title={
                <EuiFlexGroup direction='row' justifyContent='spaceBetween' alignItems='center' gutterSize='xs'>
                    <EuiFlexItem grow={10}>
                        <EuiFlexGroup justifyContent='flexStart' alignItems='center' direction='row' gutterSize='s'>
                            <EuiFlexItem grow={false}>
                                <EuiIcon type='starFilledSpace' size='l' />
                            </EuiFlexItem>
                            <EuiFlexItem style={{ cursor: 'pointer' }} onClick={() => setIsNavOpen(!isNavOpen)}>
                                <EuiTitle size='s'>
                                    <h3>Tier System</h3>
                                </EuiTitle>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiButtonIcon
                            iconType={isNavOpen ? 'arrowUp' : 'arrowDown'}
                            onClick={() => setIsNavOpen(!isNavOpen)}
                            aria-label='Toggle tier system settings'
                        />
                    </EuiFlexItem>
                </EuiFlexGroup>
            }
        >
            <EuiCollapsibleNavGroup
                title={<></>}
                isCollapsible={true}
                initialIsOpen={isNavOpen}
                forceState={isNavOpen ? 'open' : 'closed'}
                arrowDisplay='none'
                key={`tier-nav-${enableTierSystem}`} // Force re-render when toggle changes
            >
                <SwitchField
                    name='enable_tier_system'
                    defaultValue={enableTierSystem || false}
                    control={control}
                    label='Enable Tier System'
                    setValue={setValue}
                />

                {enableTierSystem ? (
                    <>
                        {console.log('[TierSystem] Rendering tier content - tierFields:', tierFields)}
                        <EuiSpacer size='l' />

                        <EuiFlexGroup gutterSize='l'>
                            <EuiFlexItem grow={false}>
                                <EuiFormRow label='Number of Tiers'>
                                    <EuiSelect
                                        options={tierCountOptions}
                                        value={tierCount}
                                        onChange={(e) => {
                                            const newCount = parseInt(e.target.value);
                                            setTierCount(newCount);
                                            setValue('tier_count', newCount, { shouldDirty: true });
                                        }}
                                    />
                                </EuiFormRow>
                            </EuiFlexItem>
                            <EuiFlexItem>
                                <NumberField
                                    label='Rolling Period for Downgrades'
                                    name='tier_downgrade_period_days'
                                    control={control}
                                    errors={errors}
                                    placeholder='365'
                                    append='days'
                                    helpText='Evaluate tier status based on spending in this period'
                                    rules={{
                                        required: 'Period required',
                                        min: { value: 30, message: 'Min 30 days' },
                                        max: { value: 730, message: 'Max 730 days' },
                                    }}
                                />
                            </EuiFlexItem>
                        </EuiFlexGroup>

                        <EuiSpacer size='l' />

                        <EuiPanel>
                            <EuiTitle size='xs'>
                                <h4>Tier Configuration</h4>
                            </EuiTitle>
                            <EuiSpacer size='m' />

                            {tierFields.map((field, index) => (
                                <React.Fragment key={field.id}>
                                    <EuiPanel color='subdued' paddingSize='m'>
                                        <EuiFlexGroup>
                                            <EuiFlexItem>
                                                <TextField
                                                    label='Tier Name'
                                                    name={`tiers.${index}.name`}
                                                    control={control}
                                                    errors={errors}
                                                    placeholder={defaultTierNames[index]}
                                                    rules={{
                                                        required: 'Tier name required',
                                                    }}
                                                />
                                            </EuiFlexItem>
                                            <EuiFlexItem>
                                                <NumberField
                                                    label='LTV From'
                                                    name={`tiers.${index}.ltv_threshold`}
                                                    control={control}
                                                    errors={errors}
                                                    placeholder={defaultTierThresholds[index].toString()}
                                                    prepend='₹'
                                                    rules={{
                                                        required: 'Threshold required',
                                                        validate: (value) => validateTierThreshold(value, index),
                                                    }}
                                                    readOnly={index === 0}
                                                />
                                            </EuiFlexItem>
                                        </EuiFlexGroup>

                                        <EuiSpacer size='m' />

                                        <EuiText size='s'>
                                            <strong>Benefits:</strong>
                                        </EuiText>
                                        <EuiSpacer size='s' />

                                        {selectedSystem === 'cashback' ? (
                                            <NumberField
                                                label='Cashback Rate'
                                                name={`tiers.${index}.cashback_rate`}
                                                control={control}
                                                errors={errors}
                                                placeholder={defaultCashbackRates[index].toString()}
                                                append='%'
                                                rules={{
                                                    required: 'Rate required',
                                                    min: { value: 0.1, message: 'Min 0.1%' },
                                                    max: { value: 100, message: 'Max 100%' },
                                                }}
                                                step='any'
                                            />
                                        ) : (
                                            <EuiFlexGroup gutterSize='m' alignItems='flexEnd'>
                                                <EuiFlexItem grow={false}>
                                                    <EuiText size='s'>Points Earning:</EuiText>
                                                </EuiFlexItem>
                                                <EuiFlexItem grow={false}>
                                                    <NumberField
                                                        name={`tiers.${index}.points_earning_points`}
                                                        control={control}
                                                        errors={errors}
                                                        placeholder='1'
                                                        compressed
                                                        rules={{
                                                            required: 'Points required',
                                                            min: { value: 1, message: 'Min 1' },
                                                        }}
                                                    />
                                                </EuiFlexItem>
                                                <EuiFlexItem grow={false}>
                                                    <EuiText size='s'>points per ₹</EuiText>
                                                </EuiFlexItem>
                                                <EuiFlexItem grow={false}>
                                                    <NumberField
                                                        name={`tiers.${index}.points_earning_amount`}
                                                        control={control}
                                                        errors={errors}
                                                        placeholder={defaultPointsAmounts[index].toString()}
                                                        compressed
                                                        rules={{
                                                            required: 'Amount required',
                                                            min: { value: 1, message: 'Min ₹1' },
                                                        }}
                                                    />
                                                </EuiFlexItem>
                                            </EuiFlexGroup>
                                        )}
                                    </EuiPanel>

                                    {index < tierFields.length - 1 && <EuiHorizontalRule margin='s' />}
                                </React.Fragment>
                            ))}
                        </EuiPanel>
                    </>
                ) : (
                    <>
                        {console.log(
                            '[TierSystem] NOT rendering tier content - enableTierSystem is:',
                            enableTierSystem
                        )}
                    </>
                )}
            </EuiCollapsibleNavGroup>
        </EuiCard>
    );
};

export default TierSystem;
